import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useConferenceStore } from '@/stores/conference.store';
import { conferenceJoinBlocker, useVoipStore } from '@/stores/voip.store';
import { conferenceApi } from './api';
import { isConferenceOver } from './state';
import type { ConferenceState } from './types';

const keys = {
  state: (id: string) => ['conference', id] as const,
  roster: () => ['conference', 'roster'] as const,
};

/** Surface the calling service's actual message (auth / 409 no-SIP / 503 kill-
 *  switch / provisioning) so a failed action is never silent. */
const toastError = (fallback: string) => (e: unknown) =>
  toast.error(e instanceof Error && e.message ? e.message : fallback);

/** Team members available to start with / add. */
export function useRoster() {
  return useQuery({
    queryKey: keys.roster(),
    queryFn: () => conferenceApi.roster(),
    staleTime: 60_000,
  });
}

/**
 * Start an internal call with the selected team members, the same way the
 * mobile app does: the service prepares a room, this tab dials into it, and the
 * service rings everyone else once our leg arrives. `hostName` is what the
 * invited members see ringing.
 */
export function useStartConference() {
  return useMutation({
    mutationFn: async (vars: { targetUserIds: string[]; hostName?: string }) => {
      // Refuse before creating anything, so a busy or unregistered tab doesn't
      // leave a room behind for a call that was never going to be placed.
      const blocked = conferenceJoinBlocker(useVoipStore.getState());
      if (blocked) throw new Error(blocked);

      const invite = await conferenceApi.startInternalCall(vars.targetUserIds, vars.hostName);
      const refused = useVoipStore
        .getState()
        .joinConferenceRoom(invite.dialTarget, invite.callerId);
      if (refused) {
        // Nobody will dial in now; end the room rather than let it lapse.
        await conferenceApi.end(invite.conferenceId).catch(() => undefined);
        throw new Error(refused);
      }
      useConferenceStore.getState().setInvitedUserIds(vars.targetUserIds);
      return { conferenceId: invite.conferenceId };
    },
    onError: toastError('Could not start the call.'),
  });
}

/**
 * Live conference state, polled while the room is active (mirrors the customer
 * tracking page pattern). Polling stops once the room is terminal.
 */
export function useConferenceState(conferenceId: string | null) {
  return useQuery<ConferenceState>({
    queryKey: conferenceId ? keys.state(conferenceId) : ['conference', 'none'],
    queryFn: () => conferenceApi.getState(conferenceId as string),
    enabled: Boolean(conferenceId),
    refetchInterval: (query) => {
      // Keep polling while ANYONE is still live, even if the room reports a
      // terminal status — otherwise we'd freeze on a stale snapshot mid-call.
      return isConferenceOver(query.state.data) ? false : 1500;
    },
  });
}

/** Participant + room controls for an active conference. */
export function useConferenceControls(conferenceId: string) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: keys.state(conferenceId) });

  return {
    addParticipant: useMutation({
      mutationFn: (userId: string) => conferenceApi.addParticipant(conferenceId, userId),
      onSuccess: (_result, userId) => {
        useConferenceStore.getState().addInvitedUserId(userId);
        return refresh();
      },
      onError: toastError('Could not add that member.'),
    }),
    addPstn: useMutation({
      mutationFn: (e164: string) => conferenceApi.addPstn(conferenceId, e164),
      onSuccess: refresh,
      onError: toastError('Could not add that phone number.'),
    }),
    setMuted: useMutation({
      mutationFn: (v: { ccid: string; muted: boolean }) =>
        conferenceApi.setMuted(conferenceId, v.ccid, v.muted),
      onSuccess: refresh,
      onError: toastError('Could not change mute.'),
    }),
    setHold: useMutation({
      mutationFn: (v: { ccid: string; onHold: boolean }) =>
        conferenceApi.setHold(conferenceId, v.ccid, v.onHold),
      onSuccess: refresh,
      onError: toastError('Could not change hold.'),
    }),
    remove: useMutation({
      mutationFn: (ccid: string) => conferenceApi.remove(conferenceId, ccid),
      onSuccess: refresh,
      onError: toastError('Could not remove that participant.'),
    }),
    end: useMutation({
      mutationFn: () => conferenceApi.end(conferenceId),
      onSuccess: refresh,
      onError: toastError('Could not end the conference.'),
    }),
  };
}
